import React from "react";
import { ShieldAlert, Lock, Layers, GitBranch, ScrollText } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal, Eyebrow } from "@/components/Reveal";

const CONSTITUTION = [
  {
    n: "Article I",
    t: "Purpose",
    d: "The 7 Generations Indigenous Trust exists to preserve heritage, steward lineage, and build independent prosperity for descendants of Woodland Mississippian and Modern Freedmen ancestry who hold verified lineage yet remain outside federal and state tribal frameworks.",
  },
  {
    n: "Article II",
    t: "Membership",
    d: "Membership is open to independent descendants who anchor verified genealogy through the Vault. Every member is a constituent of equal standing, holds a digital economic citizenship credential, and retains full ownership of their own records.",
  },
  {
    n: "Article III",
    t: "Self-Custody of Records",
    d: "All genealogies, archives, and credentials are family-controlled, encrypted, and permanently anchored on a decentralized registry. No external authority may alter, constrain, or revoke a member's record.",
  },
  {
    n: "Article IV",
    t: "The Three Pillars",
    d: "The Trust is governed by three enduring pillars — Preservation of culture and memory, Stewardship of lineage and shared assets, and Prosperity through independent economic instruments.",
  },
  {
    n: "Article V",
    t: "The Seven Generations Principle",
    d: "Every decision is weighed against its impact seven generations forward. The Trust acts as patient, multigenerational capital — never sacrificing the lineage of tomorrow for the convenience of today.",
  },
  {
    n: "Article VI",
    t: "Sovereignty by Architecture",
    d: "Autonomy is secured through decentralized architecture rather than external permission. The Trust holds its own records, issues its own credentials, and governs its own assets, free from Projected Sovereignty.",
  },
  {
    n: "Article VII",
    t: "Amendment & Continuity",
    d: "This constitution endures across generations. Amendments preserve the founding purpose and the rights of members, ensuring institutional permanence and unbroken continuity of the lineage.",
  },
];

export default function Structure({ embedded }) {
  return (
    <div>
      {!embedded && (
        <PageHeader
          eyebrow="Structure & Institutional Philosophy"
          title="Why private institutional architecture outlasts federal trust constraints."
          subtitle="The Trust is designed to avoid 'Projected Sovereignty' — dependence on recognition granted, and revocable, by external authority."
        />
      )}

      <section className="py-24 bg-ink">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-start">
          <Reveal>
            <h2 className="font-serif font-light text-4xl tracking-tight leading-tight">
              Autonomy by architecture, not by permission.
            </h2>
            <div className="space-y-6 mt-8 font-sans text-[#A19D98] leading-relaxed">
              <p>
                Traditional federal trusts bind descendants to constraints they did not author and cannot
                amend. Recognition becomes something granted from the outside — and what is granted can be
                withdrawn. We call this <span className="text-gold-text">Projected Sovereignty</span>, and
                we are built to avoid it.
              </p>
              <p>
                By operating as a private institution atop a decentralized blockchain registry, the Trust
                holds its own records, issues its own credentials, and governs its own assets. Superior
                long-term autonomy comes from architecture that no external body controls.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-gold/20 bg-ink-surface divide-y divide-gold/10">
              {[
                { icon: ShieldAlert, t: "Avoids Projected Sovereignty", d: "No dependence on recognition that can be granted, constrained, or revoked by others." },
                { icon: Lock, t: "Self-Custodied Records", d: "Genealogies and archives are family-controlled, encrypted, and permanently anchored." },
                { icon: Layers, t: "Institutional Permanence", d: "Structured for continuity across generations — patient, durable, and sovereign-minded." },
                { icon: GitBranch, t: "Decentralized Governance", d: "Distributed architecture removes single points of external control or failure." },
              ].map((row) => (
                <div key={row.t} className="flex items-start gap-5 p-8">
                  <row.icon size={22} className="text-gold shrink-0 mt-1" strokeWidth={1.4} />
                  <div>
                    <h3 className="font-serif text-xl">{row.t}</h3>
                    <p className="font-sans text-sm text-[#A19D98] leading-relaxed mt-2">{row.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 bg-ink-deep">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="flex justify-center" data-testid="three-pillars-seal">
              <img
                src="/three-pillars-seal.jpg"
                alt="The Three Pillars seal — Preservation, Stewardship, Prosperity"
                className="w-52 sm:w-64 h-auto object-contain mix-blend-lighten drop-shadow-[0_0_40px_rgba(197,160,80,0.25)]"
              />
            </div>
          </Reveal>
          <Reveal><Eyebrow className="mt-10 block text-center">The Three Pillars</Eyebrow></Reveal>
          <div className="grid md:grid-cols-3 gap-px mt-12 border border-gold/15 bg-gold/10">
            {[
              { n: "I", t: "Preservation", s: "Cultural & Historical", d: "Immutable safekeeping of heritage, memory, and archives." },
              { n: "II", t: "Stewardship", s: "Lineage & Assets", d: "Multigenerational management of records and shared assets." },
              { n: "III", t: "Prosperity", s: "Economic Sovereignty", d: "Independent instruments converting heritage into autonomy." },
            ].map((p, i) => (
              <Reveal key={p.t} delay={i * 0.12} className="bg-ink">
                <div className="p-10 h-full">
                  <span className="font-serif text-5xl text-gold-gradient">{p.n}</span>
                  <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-gold/60 mt-6">{p.s}</p>
                  <h3 className="font-serif text-3xl mt-1">{p.t}</h3>
                  <p className="font-sans text-sm text-[#A19D98] leading-relaxed mt-4">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold/60 mt-12 text-center">
              Preservation • Stewardship • Prosperity
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 bg-ink" data-testid="constitution-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="flex items-center gap-4">
              <ScrollText size={26} className="text-gold shrink-0" strokeWidth={1.3} />
              <Eyebrow>Constitution of the Trust</Eyebrow>
            </div>
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-tight mt-6">
              A living charter, drawn from our mission.
            </h2>
            <div className="border-l-2 border-gold/40 pl-6 mt-10">
              <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-gold/60">Preamble</p>
              <p className="font-serif italic text-xl sm:text-2xl text-[#E8D399] leading-relaxed mt-3">
                We, the descendants of the Woodland Mississippian peoples and the Modern Freedmen, establish
                this Trust to preserve our heritage, steward our lineage, and secure independent prosperity —
                for ourselves and for the seven generations yet to come.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 border border-gold/20 bg-ink-surface divide-y divide-gold/10">
            {CONSTITUTION.map((art, i) => (
              <Reveal key={art.n} delay={i * 0.06}>
                <div className="grid sm:grid-cols-12 gap-4 p-8" data-testid={`constitution-article-${i + 1}`}>
                  <div className="sm:col-span-3">
                    <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/60">{art.n}</p>
                    <h3 className="font-serif text-2xl text-[#F5F0EB] mt-1">{art.t}</h3>
                  </div>
                  <p className="sm:col-span-9 font-sans text-sm text-[#A19D98] leading-relaxed">{art.d}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold/60 mt-12 text-center">
              Ratified in the spirit of Preservation • Stewardship • Prosperity
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
